import type { BadgeVariant } from "@/v2-backoffice-client/components/badge"
import type { PrivacyChangeKindValue } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

/**
 * The eight person fields a policy row scores, by their
 * `acc.PrivateDataFields.ColumnName` — which is BOTH the key the catalogue
 * hands back per column AND the field name the policy types carry the code
 * under, so `policy[field.ColumnName]` is the cell value with no mapping
 * table in between.
 */
export const POLICY_FIELD_KEYS = [
  "NationalId",
  "BornYear",
  "BornAt",
  "Name",
  "GenderCode",
  "PhoneNumber",
  "HomeAddress",
  "HomePostalCode",
] as const

/** Mirrors acc.TenantsPrivateDataVisibilityPolicies.Justification NVARCHAR(1000). */
export const PRIVACY_JUSTIFICATION_MAX_LENGTH = 1000

/** Visible label in the preset dialog; accessible name of the footer input. */
export const PRIVACY_JUSTIFICATION_LABEL = "Justification (optional)"

/** What the text is for — the dialog shows it as the hint under the label. */
export const PRIVACY_JUSTIFICATION_HINT = "Kept in the change history"

/** The table footer has no visible label, so its placeholder carries both. */
export const PRIVACY_JUSTIFICATION_PLACEHOLDER = `${PRIVACY_JUSTIFICATION_LABEL} — ${PRIVACY_JUSTIFICATION_HINT}`

/**
 * The label the policy pill carries everywhere it appears — the teams list
 * column, the team header field and the editor's status row. Neutral on
 * purpose: "Personal data" names what the policy governs, where "Privacy
 * policy" reads as a legal document.
 */
export const PRIVACY_POLICY_LABEL = "Personal data"

/** What a summary that matches no preset is called, wherever it is shown. */
export const PRIVACY_CUSTOM_POLICY_LABEL = "Custom"

/**
 * The GraphQL TenantPrivateDataVisibilityChangeKind enum as it reaches the
 * client (string values), as literals so call sites never import the
 * generated enum object at runtime — same contract as tenantMigration.
 */
export type PrivacyChangeKindLiteral = `${PrivacyChangeKindValue}`

const PRIVACY_CHANGE_KIND_CONFIGURED: PrivacyChangeKindLiteral = "CONFIGURED"
const PRIVACY_CHANGE_KIND_UPDATED: PrivacyChangeKindLiteral = "UPDATED"
export const PRIVACY_CHANGE_KIND_REMOVED: PrivacyChangeKindLiteral = "REMOVED"

/** Display order for the per-kind counts on a history event. */
export const PRIVACY_CHANGE_KINDS: PrivacyChangeKindLiteral[] = [
  PRIVACY_CHANGE_KIND_CONFIGURED,
  PRIVACY_CHANGE_KIND_UPDATED,
  PRIVACY_CHANGE_KIND_REMOVED,
]

type PrivacyChangeKindStyle = {
  label: string
  variant: BadgeVariant
}

/**
 * One label + tone per kind of change a write makes to a source's row.
 * Keyed exhaustively: a new enum value without a style is a compile error,
 * not a silent unlabelled pill. Removal is the loud one because it is the
 * change nobody is credited with (the procedures stamp no user on a delete).
 */
export const PRIVACY_CHANGE_KIND_STYLES: Record<
  PrivacyChangeKindLiteral,
  PrivacyChangeKindStyle
> = {
  [PRIVACY_CHANGE_KIND_CONFIGURED]: {
    label: "Configured",
    variant: "positive",
  },
  [PRIVACY_CHANGE_KIND_UPDATED]: { label: "Updated", variant: "neutral" },
  [PRIVACY_CHANGE_KIND_REMOVED]: { label: "Removed", variant: "warning" },
}
